import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Flame, Clock, TrendingUp, Loader2, Zap, SlidersHorizontal, ArrowRight } from 'lucide-react';
import { DealCard } from '../components/DealCard';
import { BannerCarousel, EsqueletoHero, FaixaDestaques, fetchSidebarBanners, carregarBannersHero } from '../components/BannerCarousel';
import AnuncioGrade from '../components/anuncios/AnuncioGrade';
import { ADSENSE } from '../config/adsense';
import { Button } from '../components/ui/button';
import { API } from '../config/apiBase';
import { useFeature, useFeaturesCarregadas } from '../context/FeaturesContext';
import PromotionsHub from '../components/PromotionsHub';
import MarketplaceStrip from '../components/vitrine/MarketplaceStrip';
import DealRail from '../components/vitrine/DealRail';
import SectionHeader from '../components/vitrine/SectionHeader';
import { useDecisaoHero } from '../components/vitrine/decisaoHero';
import { useSinalDeCurtidas } from '../components/vitrine/sinalCurtidas';
import useCatalogoInfinito from '../hooks/useCatalogoInfinito';
import { registrarFiltro } from '../inteligencia/coleta';

// Lotes que o feed busca sozinho enquanto a pessoa rola: 4 lotes de 12 além dos
// 12 iniciais, 60 ofertas no total (4 anúncios na cadência de 15). No celular são
// ~10 mil px de lista (~12 telas); no desktop de 5 colunas, 12 fileiras. Daí em
// diante o feed para e mostra "Carregar mais ofertas" antes do rodapé: sem esse
// limite o rodapé nunca ficava na tela (o catálogo tem milhares de ofertas e
// cada lote novo o empurrava).
export const FEED_TAMANHO_LOTE = 12;
export const FEED_LOTES_AUTOMATICOS = 4;

/**
 * Home do site público.
 *
 * Ordem da página, de cima para baixo:
 *   1. hero: banners em destaque, ou as campanhas da Central quando ela está
 *      ligada e tem campanha ativa (nunca os dois);
 *   2. Central de Promoções (relâmpago e destaques por loja), só com a flag;
 *   3. atalhos por loja;
 *   4. seções editoriais, cada uma alimentada por uma consulta real;
 *   5. o feed completo, com as mesmas abas, filtro e rolagem infinita de antes.
 *
 * As seções editoriais foram escolhidas pelo que os dados de hoje sustentam. Em
 * 2026-09-12, 6.558 das 6.567 ofertas compráveis não tinham categoria e nenhuma
 * tinha curtida, então seção por categoria ou "mais curtidas" mostraria ordem
 * arbitrária com cara de curadoria. Ficaram só as que têm critério de verdade:
 * desconto, preço baixo e loja.
 */

// `hot` ordena por curtidas e só aparece quando existe sinal real de curtidas
// (ver vitrine/sinalCurtidas.js).
const TABS = [
    { key: 'hot', label: 'Destaques', icone: Zap, exigeCurtidas: true },
    { key: 'recent', label: 'Recentes', icone: Clock },
    // A ordenação `discount` aparecia rotulada "Em alta", o que não descreve o
    // que ela faz. O filtro é o mesmo; só o nome passou a dizer a verdade.
    { key: 'discount', label: 'Maiores descontos', icone: Flame },
    { key: 'price', label: 'Menor preço', icone: TrendingUp },
];

export default function HomePage() {
    const showPlans = useFeature('show_plans');
    const promotionsHub = useFeature('promotions_hub');
    const featuresCarregadas = useFeaturesCarregadas();
    const [searchParams, setSearchParams] = useSearchParams();
    const [categories, setCategories] = useState([]);
    const [categoriasCarregadas, setCategoriasCarregadas] = useState(false);
    const [activeTab, setActiveTab] = useState('recent');
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [gridBanners, setGridBanners] = useState([]);
    const feedRef = useRef(null);

    const categoryFilter = searchParams.get('categoria');
    const sortFilter = searchParams.get('sort') || 'recent';
    const hero = useDecisaoHero();
    const sinalCurtidas = useSinalDeCurtidas();
    const abas = TABS.filter((t) => !t.exigeCurtidas || sinalCurtidas === true);
    // A coluna "Acabaram de chegar" do hero é a mesma consulta do feed em
    // "Recentes". Quando o feed abre nessa ordem, a coluna usa a resposta dele.
    const [recentesHero, setRecentesHero] = useState(
        () => (sortFilter === 'recent' && !categoryFilter ? null : undefined),
    );

    useEffect(() => {
        fetchCategories();
        fetchSidebarBanners().then(setGridBanners);
        // Adianta os banners do hero enquanto a Home decide quem ocupa o topo;
        // a resposta fica em cache e também serve à faixa de destaques.
        carregarBannersHero();
    }, []);

    // Link antigo com ?sort=hot e sem curtidas de verdade cai em "Recentes".
    useEffect(() => {
        if (sortFilter !== 'hot' || sinalCurtidas !== false) return;
        const p = new URLSearchParams(searchParams);
        p.set('sort', 'recent');
        setSearchParams(p, { replace: true });
    }, [sortFilter, sinalCurtidas, searchParams, setSearchParams]);

    useEffect(() => {
        if (sortFilter) setActiveTab(sortFilter);
    }, [sortFilter]);

    // Feed: mesma API, mesmos filtros e mesma ordenação de antes.
    const montarUrlFeed = useCallback((skip) => {
        let url = `${API}/produtos?sort=${sortFilter}&limit=${FEED_TAMANHO_LOTE}&skip=${skip}`;
        if (categoryFilter) url += `&category=${categoryFilter}`;
        return url;
    }, [sortFilter, categoryFilter]);
    const aoCarregarPrimeira = useCallback((novos) => {
        if (novos === null) { setRecentesHero((atual) => (atual === null ? [] : atual)); return; }
        if (sortFilter === 'recent' && !categoryFilter) setRecentesHero((atual) => atual || novos);
    }, [sortFilter, categoryFilter]);
    const feed = useCatalogoInfinito({
        montarUrl: montarUrlFeed,
        chave: `${categoryFilter || ''}|${sortFilter}`,
        lotesAutomaticos: FEED_LOTES_AUTOMATICOS,
        aoCarregarPrimeira,
    });
    const { itens: deals, carregando: loading, carregandoMais: loadingMore, temMais: hasMore } = feed;

    const fetchCategories = async () => {
        try {
            const response = await fetch(`${API}/categories`);
            const data = await response.json();
            setCategories(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching categories:', error);
        } finally {
            setCategoriasCarregadas(true);
        }
    };

    const handleTabChange = (tab) => {
        if (tab !== activeTab) registrarFiltro('sort', tab);
        setActiveTab(tab);
        const newParams = new URLSearchParams(searchParams);
        newParams.set('sort', tab);
        setSearchParams(newParams);
    };

    const irParaFeed = (tab) => {
        handleTabChange(tab);
        feedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const achadosBaratos = categories.find((c) => c.slug === 'low-ticket');
    const nomeCategoria = categories.find(c => c.category_id === categoryFilter)?.name;

    return (
        <div className="min-h-screen pb-4" data-testid="home-page">
            {/* 1. Hero. Esqueleto até saber quem ocupa o topo; depois banners, ou
                nada aqui quando a campanha da Central assume. Nunca os dois. */}
            {!categoryFilter && hero.estado !== 'campanha' && (
                <section className="pt-5 sm:pt-7">
                    <div className="site-container mx-auto px-4 sm:px-6 lg:px-8">
                        {hero.estado === 'carregando' ? <EsqueletoHero /> : <BannerCarousel recentes={recentesHero} />}
                    </div>
                </section>
            )}

            {/* 2. Central de Promoções e Destaques — campanhas, relâmpago e lojas.
                Só com a flag ligada, e só depois da decisão do hero: recebe a
                resposta já carregada e desenha a campanha no mesmo quadro em
                que o esqueleto sai. */}
            {!categoryFilter && promotionsHub && hero.estado !== 'carregando' && (
                <PromotionsHub dadosIniciais={hero.promocoes} />
            )}

            <div className="site-container mx-auto px-4 sm:px-6 lg:px-8">
                {/* 3. Atalhos por loja. Com a Central ligada, os Destaques por loja já
                    levam às mesmas quatro lojas, com contagem; mostrar os dois repete.
                    Espera as flags para não aparecer e sumir com a Central ligada. */}
                {!categoryFilter && featuresCarregadas && !promotionsHub && <MarketplaceStrip />}
                {!categoryFilter && !featuresCarregadas && <MarketplaceStrip esqueleto />}

                {/* Trial CTA Banner */}
                {!categoryFilter && showPlans && (
                    <Link to="/planos" className="sv-card sv-section flex flex-row items-center justify-between gap-4 p-4 sm:p-5" data-testid="trial-banner">
                        <div className="flex-1 min-w-0">
                            <p className="text-sm sm:text-base font-bold" style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}>
                                7 dias grátis no plano Start
                            </p>
                            <p className="text-[12px] sm:text-sm mt-0.5" style={{ color: 'var(--muted)' }}>
                                Ponto Digital, até 3 colaboradores, gestão completa. Sem cartão de crédito.
                            </p>
                        </div>
                        <span className="sv-btn-action !py-2 !text-[13px]">Testar grátis <ArrowRight size={14} /></span>
                    </Link>
                )}

                {/* 4. Seções editoriais — só com critério real */}
                {!categoryFilter && (
                    <>
                        <DealRail
                            testId="secao-descontos"
                            titulo="Maiores descontos agora"
                            subtitulo="As ofertas com a maior diferença entre o preço de antes e o de hoje."
                            query="sort=discount"
                            superficie="home_maiores_descontos"
                            carregarJa
                            acao={{ label: 'Ver todas', onClick: () => irParaFeed('discount') }}
                        />
                        {/* Enquanto as categorias não chegam, a seção ocupa o lugar com
                            esqueleto. Sem isso, os trilhos de baixo subiam ~500 px na
                            primeira pintura, entravam na margem do carregamento sob
                            demanda e eram buscados sem o visitante rolar. */}
                        {(achadosBaratos || !categoriasCarregadas) && (
                            <DealRail
                                testId="secao-baratos"
                                layout="lista"
                                superficie="home_achados"
                                titulo="Achados baratos"
                                subtitulo="Os menores preços do catálogo, do mais barato para cima."
                                query={achadosBaratos ? `category=${encodeURIComponent(achadosBaratos.category_id)}&sort=price` : null}
                                acao={achadosBaratos ? { label: 'Ver todos', to: `/?categoria=${encodeURIComponent(achadosBaratos.category_id)}&sort=price` } : undefined}
                            />
                        )}
                        <FaixaDestaques banners={gridBanners} />
                        <DealRail
                            testId="secao-mercadolivre"
                            titulo="Mercado Livre em oferta"
                            subtitulo="O que mais caiu de preço na maior loja do catálogo."
                            query="marketplace=mercadolivre&sort=discount"
                            superficie="home_mercadolivre"
                            acao={{ label: 'Ver loja', to: '/busca?marketplace=mercadolivre' }}
                        />
                        <DealRail
                            testId="secao-amazon"
                            titulo="Selecionados na Amazon"
                            subtitulo="Ofertas da Amazon com link direto para o produto."
                            query="marketplace=amazon"
                            superficie="home_amazon"
                            acao={{ label: 'Ver loja', to: '/busca?marketplace=amazon' }}
                        />
                    </>
                )}

                {/* 5. Feed completo */}
                <main className="sv-section scroll-mt-28" data-testid="deals-feed" ref={feedRef}>
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-5">
                        <SectionHeader
                            titulo={categoryFilter ? (nomeCategoria || 'Categoria') : 'Todas as ofertas'}
                            subtitulo={categoryFilter ? 'Ofertas desta categoria.' : 'Tudo o que entrou na vitrine, do jeito que você preferir ver.'}
                        />

                        <div className="sv-feed-abas relative flex items-center gap-2 overflow-x-auto sv-rail -mx-4 px-4 lg:mx-0 lg:px-0 lg:overflow-visible pb-1 lg:pb-0 lg:mb-[18px]">
                            {abas.map(({ key, label, icone: Icone }) => (
                                <button
                                    key={key}
                                    onClick={() => handleTabChange(key)}
                                    className={`sv-chip shrink-0 ${activeTab === key ? 'is-active' : ''}`}
                                    aria-pressed={activeTab === key}
                                    data-testid={`tab-${key}`}
                                >
                                    <Icone className="w-4 h-4" />
                                    {label}
                                </button>
                            ))}
                            <div className="relative shrink-0">
                                <button
                                    onClick={() => setShowFilterMenu(!showFilterMenu)}
                                    className={`sv-chip ${showFilterMenu || categoryFilter ? 'is-active' : ''}`}
                                    aria-expanded={showFilterMenu}
                                    aria-label="Filtrar por categoria"
                                    data-testid="filter-btn"
                                >
                                    <SlidersHorizontal className="w-4 h-4" />
                                    <span className="hidden sm:inline">Categorias</span>
                                </button>
                                {showFilterMenu && (
                                    <div className="absolute right-0 top-full mt-2 z-50 rounded-2xl py-2 min-w-[220px] max-h-[360px] overflow-y-auto"
                                        style={{ background: 'var(--surface)', border: '1px solid var(--border-hover)', boxShadow: 'var(--sv-shadow-lift)' }}
                                        data-testid="filter-dropdown">
                                        <p className="px-3 py-1 text-[12px] font-semibold" style={{ color: 'var(--muted)' }}>Categorias</p>
                                        <button onClick={() => { registrarFiltro('category', 'all'); setSearchParams({}); setShowFilterMenu(false); }}
                                            className="w-full px-3 py-2 text-left text-[13px] hover:bg-[var(--surface-2)] flex items-center gap-2"
                                            style={{ color: !categoryFilter ? 'var(--gold-2)' : 'var(--text)' }} data-testid="filter-all">
                                            Todas
                                        </button>
                                        {categories.map(c => (
                                            <button key={c.category_id}
                                                onClick={() => { registrarFiltro('category', c.category_id); const p = new URLSearchParams(searchParams); p.set('categoria', c.category_id); setSearchParams(p); setShowFilterMenu(false); }}
                                                className="w-full px-3 py-2 text-left text-[13px] hover:bg-[var(--surface-2)] flex items-center gap-2"
                                                style={{ color: categoryFilter === c.category_id ? 'var(--gold-2)' : 'var(--text)' }}
                                                data-testid={`filter-cat-${c.category_id}`}>
                                                {c.icon && <span>{c.icon}</span>}
                                                {c.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex flex-col sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 deal-grid-responsive gap-3 sm:gap-4 lg:gap-5" aria-busy="true" aria-label="Carregando ofertas">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <div key={i} className="sv-skeleton rounded-2xl h-[128px] sm:h-auto sm:aspect-[3/4.4]" />
                            ))}
                        </div>
                    ) : deals.length === 0 ? (
                        <div className="sv-card items-center text-center py-16 px-6">
                            <p className="mb-4 text-[15px]" style={{ color: 'var(--muted)' }}>Nenhuma oferta por aqui agora. Novas ofertas entram a cada poucos minutos.</p>
                            <Button
                                variant="outline"
                                onClick={() => { feed.recarregar(); }}
                                className="sv-btn-ghost !h-auto"
                            >
                                Atualizar
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-col sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 deal-grid-responsive gap-3 sm:gap-4 lg:gap-5">
                                {(() => {
                                    const items = [];
                                    let nAnuncio = 0;
                                    deals.forEach((deal, i) => {
                                        items.push({ type: 'deal', data: deal, dealIndex: i });
                                        // Um anúncio do Google a cada N ofertas (ADSENSE.intervaloGrade),
                                        // na célula seguinte. A chave depende só da posição, então
                                        // carregar mais ofertas não remonta os anúncios já pedidos.
                                        if ((i + 1) % ADSENSE.intervaloGrade === 0) {
                                            items.push({ type: 'anuncio', n: nAnuncio });
                                            nAnuncio++;
                                        }
                                    });
                                    return items.map((item) => {
                                        if (item.type === 'anuncio') {
                                            return <AnuncioGrade key={`anuncio-${item.n}`} posicao={item.n} />;
                                        }
                                        return (
                                            <div key={item.data.deal_id}>
                                                <DealCard deal={item.data} exposicao={{ surface: categoryFilter ? 'category' : 'home_feed', position: item.dealIndex + 1, section: sortFilter }} />
                                            </div>
                                        );
                                    });
                                })()}
                            </div>

                            {/* Fim do feed. Enquanto há ofertas, uma faixa de altura fixa
                                (88 px) serve de gatilho da rolagem automática e mostra o
                                carregando; esgotados os lotes automáticos, mostra o botão.
                                A altura não muda entre esses estados: nada empurra o rodapé. */}
                            {hasMore && (
                                <div ref={feed.sentinelaRef} className="sv-feed-mais" data-testid="feed-mais">
                                    {feed.automaticoEsgotado ? (
                                        <button type="button" onClick={feed.carregarMais} disabled={loadingMore} aria-busy={loadingMore}
                                            className="sv-btn-ghost" data-testid="feed-carregar-mais">
                                            {loadingMore && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                                            {loadingMore ? 'Carregando ofertas' : 'Carregar mais ofertas'}
                                        </button>
                                    ) : loadingMore ? (
                                        <span className="inline-flex" aria-live="polite">
                                            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--gold-2)' }} aria-hidden="true" />
                                            <span className="sr-only">Carregando mais ofertas</span>
                                        </span>
                                    ) : null}
                                </div>
                            )}
                        </>
                    )}

                    {!loading && !hasMore && deals.length > 0 && (
                        <p className="mt-10 pt-6 text-center text-sm" style={{ borderTop: '1px solid var(--border)', color: 'var(--muted)' }}>
                            Você viu todas as {deals.length} ofertas disponíveis.
                        </p>
                    )}
                </main>
            </div>
        </div>
    );
}
